import type { Workspace } from './types';

/**
 * What the caller may do from a workspace's sidebar menus, read from the
 * server-computed `myRole` on the workspace list (WS-LIST-MY-ROLE-1). A missing
 * or `none` role grants nothing: the menus fail closed, they never infer a role
 * from the org role. `canAdminister` is true for an org admin and is used only for
 * archive and delete, which the server restricts to an org admin.
 */
export type WorkspaceMenuAccess = {
  /** Owner-only items: settings, rename, invite. */
  isOwner: boolean;
  /** Create items: new project, document, dashboard. */
  canCreate: boolean;
  /** Personal items (favorite, copy link) and browsing templates. */
  isMember: boolean;
  /**
   * New project (template instantiate). FR-2026-11-04-2: an org admin or a workspace owner only;
   * the server needs workspace write, which an org admin holds only with a membership row (A4).
   */
  canStartProject: boolean;
  /**
   * Archive and delete. The server allows these for an org admin only (a workspace
   * owner cannot remove the container), so they follow the server's `canAdminister`,
   * not `myRole` (ruling 6624166949).
   */
  canAdminister: boolean;
};

export function workspaceMenuAccess(
  workspace: Pick<Workspace, 'myRole' | 'canAdminister'> | null | undefined,
): WorkspaceMenuAccess {
  const role = workspace?.myRole;
  return {
    canAdminister: workspace?.canAdminister === true,
    isOwner: role === 'owner',
    canCreate: role === 'owner' || role === 'member',
    isMember: role === 'owner' || role === 'member' || role === 'viewer',
    canStartProject:
      role === 'owner' || (workspace?.canAdminister === true && (role === 'member' || role === 'viewer')),
  };
}
